import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  Icon,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Progress,
  ProgressIndicator,
  ProgressTrack,
  Switch,
  Text,
  type Color,
} from "@salestrak/ui";
import { EntityTag } from "#app/components/entity";
import {
  AdminCreateFeatureFlagScopeDocument,
  AdminDeleteFeatureFlagScopeDocument,
  AdminFeatureFlagScopesDocument,
  AdminFeatureFlagStatusesDocument,
  AdminUpdateFeatureFlagScopeDocument,
  AdminUpsertFeatureFlagDocument,
} from "#app/lib/data";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  HeaderLabel,
  InitialsAvatar,
  PanelCard,
  ScreenHeader,
  Segmented,
  StatusDot,
  TableHeaderRow,
  TableRow,
} from "./admin-ui";
import { useAdminFetch } from "./admin-shared";
import type {
  AdminWorkspace,
  DbFlag,
  FlagScopeType,
} from "./use-admin-data";
import type { Omittable } from "@salestrak/ui";

const ROLLOUT_PRESETS = [0, 25, 50, 75, 100];

type FlagPatch = Partial<
  Pick<DbFlag, "name" | "description" | "enabled" | "scope" | "rolloutPercentage">
>;

type ScopeStatus = {
  scopeId: string;
  enabled: boolean;
  inRollout: boolean;
  source: string;
  overrideId: string | null;
};

type LookupRow = {
  id: string;
  primary: string;
  secondary: string;
};

// How the flag reaches a scope — label + colors keyed by the server `source`.
const SOURCE_META: Record<
  string,
  { label: string; tone: "success" | "muted"; dot: "success" | "muted" }
> = {
  overrideOn: { label: "On · override", tone: "success", dot: "success" },
  overrideOff: { label: "Off · override", tone: "muted", dot: "muted" },
  rollout: { label: "On · rollout", tone: "success", dot: "success" },
  notInRollout: { label: "Off · rollout", tone: "muted", dot: "muted" },
  disabled: { label: "Off · flag off", tone: "muted", dot: "muted" },
};

type SearchResult = { userId: string; name: string; email: string };

// ---------------------------------------------------------------------------
// Shared flag write helper
// ---------------------------------------------------------------------------

function useFlagMutations(refetchFlags: () => void) {
  const { flagsGql } = useAdminFetch();

  const patchFlag = useCallback(
    async (flag: DbFlag, patch: FlagPatch) => {
      try {
        // Send name/description plus only the fields actually being changed.
        // Omitting enabled / rolloutPercentage / scope when they aren't in the
        // patch lets the server keep its stored value, so e.g. flipping the
        // switch can't revert another admin's rollout or scope change. `scope`
        // is still sent for a not-yet-persisted flag so the lazy-create seeds
        // the catalog scope.
        const includeScope = patch.scope !== undefined || !flag.persisted;
        await flagsGql(AdminUpsertFeatureFlagDocument, {
          input: {
            key: flag.key,
            name: patch.name ?? flag.name,
            description: patch.description ?? flag.description,
            ...(includeScope ? { scope: patch.scope ?? flag.scope } : {}),
            ...(patch.enabled !== undefined ? { enabled: patch.enabled } : {}),
            ...(patch.rolloutPercentage !== undefined
              ? { rolloutPercentage: patch.rolloutPercentage }
              : {}),
          },
        });
        refetchFlags();
      } catch (err) {
        toastMutationError(err, "Failed to update flag");
      }
    },
    [flagsGql, refetchFlags],
  );

  return { patchFlag };
}

// ---------------------------------------------------------------------------
// Rollout fill color
// ---------------------------------------------------------------------------

// Progress paints in CSS, so the bar takes a deco token directly — no resolved
// rgb string is needed.
function rolloutColor(flag: DbFlag): Color {
  if (!flag.enabled) return "grey";
  return flag.rolloutPercentage >= 100 ? "success" : "info";
}

/** The rollout bar — one place owns the track/indicator composition. */
function RolloutBar({
  value,
  color,
  thick,
}: Omittable<{
  value: number;
  color: Color;
  thick?: boolean;
}>) {
  return (
    <Progress value={value}>
      <ProgressTrack className={thick ? "h-2" : undefined} data-color={color}>
        <ProgressIndicator className="bg-c" />
      </ProgressTrack>
    </Progress>
  );
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

function FlagListRow({
  flag,
  isLast,
  onToggle,
  onOpen,
  fill,
}: {
  flag: DbFlag;
  isLast: boolean;
  onToggle: (enabled: boolean) => void;
  onOpen: () => void;
  fill: Color;
}) {
  return (
    <TableRow last={isLast} onClick={onOpen}>
      <Box fill="flex">
        <Box direction="row" align="center" gap="xs">
          <Text size="sm" weight="semibold" truncate={1}>
            {flag.name}
          </Text>
          <Badge variant="info">
            <Text size="xs" mono>
              {flag.key}
            </Text>
          </Badge>
        </Box>
        {flag.description ? (
          <Text size="xs" variant="muted" truncate={1}>
            {flag.description}
          </Text>
        ) : null}
      </Box>
      <Box width={120} direction="row" align="center" gap="xs">
        {/* The switch's own button element stops the click bubbling to the row. */}
        <Switch checked={flag.enabled} onCheckedChange={onToggle} />
        <Text size="xs" weight="medium" variant={flag.enabled ? "success" : "muted"}>
          {flag.enabled ? "On" : "Off"}
        </Text>
      </Box>
      <Box width={150} direction="row" align="center" gap="xs">
        <Box fill="flex">
          <RolloutBar value={flag.rolloutPercentage} color={fill} />
        </Box>
        <Text size="xs" variant="muted" mono>
          {flag.rolloutPercentage}%
        </Text>
      </Box>
      <Box width={120} direction="row" align="center" gap="xs">
        <Icon
          name={flag.scope === "workspace" ? "layout-grid" : "user"}
          size="xs"
          color="muted"
        />
        <Text size="xs" variant="muted">
          {flag.scope === "workspace" ? "Per workspace" : "Per user"}
        </Text>
      </Box>
    </TableRow>
  );
}

// ---------------------------------------------------------------------------
// Detail view — targeting (per-user / per-workspace overrides)
// ---------------------------------------------------------------------------

function TargetingSection({
  flag,
  workspaces,
}: {
  flag: DbFlag;
  workspaces: AdminWorkspace[];
}) {
  const { flagsGql, loginFetch } = useAdminFetch();
  const [search, setSearch] = useState("");
  const debounced = useDebouncedValue(search, 300);
  const [rows, setRows] = useState<LookupRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statuses, setStatuses] = useState<Record<string, ScopeStatus>>({});
  const [overrideCounts, setOverrideCounts] = useState({
    enabled: 0,
    disabled: 0,
  });
  const [confirmRemoveAll, setConfirmRemoveAll] = useState(false);

  const isWorkspace = flag.scope === "workspace";
  const unit = isWorkspace ? "workspaces" : "users";

  // What the rollout percentage alone reaches (overrides apply on top).
  const rolloutReach = !flag.enabled
    ? `no ${unit} (flag off)`
    : flag.rolloutPercentage >= 100
      ? `all ${unit}`
      : flag.rolloutPercentage <= 0
        ? `no ${unit}`
        : `~${flag.rolloutPercentage}% of ${unit}`;

  // The flag's overrides of its CURRENT scope type — a leftover user override
  // on a now-workspace-scoped flag is excluded. Shared by the count + delete-all.
  const fetchMyScopes = useCallback(async (): Promise<
    { id: string; enabled: boolean }[]
  > => {
    const data = await flagsGql(AdminFeatureFlagScopesDocument, {
      key: flag.key,
    });
    return (data.featureFlagScopes ?? []).filter(
      (s) => s.scopeType === flag.scope,
    );
  }, [flag.key, flag.scope, flagsGql]);

  // Count of explicit overrides currently granting the flag (section label).
  const refreshCount = useCallback(async () => {
    try {
      const mine = await fetchMyScopes();
      setOverrideCounts({
        enabled: mine.filter((s) => s.enabled).length,
        disabled: mine.filter((s) => !s.enabled).length,
      });
    } catch {
      setOverrideCounts({ enabled: 0, disabled: 0 });
    }
  }, [fetchMyScopes]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const fetchStatuses = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        setStatuses({});
        return;
      }
      try {
        const data = await flagsGql(AdminFeatureFlagStatusesDocument, {
          key: flag.key,
          scopeIds: ids,
        });
        const map: Record<string, ScopeStatus> = {};
        for (const s of data.featureFlagStatuses ?? []) {
          map[s.scopeId] = {
            scopeId: s.scopeId,
            enabled: s.enabled,
            inRollout: s.inRollout,
            source: s.source,
            overrideId: s.overrideId ?? null,
          };
        }
        setStatuses(map);
      } catch {
        setStatuses({});
      }
    },
    // enabled + rolloutPercentage are deps so the per-row "in rollout" / source
    // badges re-resolve after the flag is toggled or its rollout changes. The
    // refetch reads them from the SERVER, not from this closure, which is why
    // the rule sees them as unused — they are the trigger, not an input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flag.key, flag.enabled, flag.rolloutPercentage, flagsGql],
  );

  // Resolve the search → display rows (capped at 10) + total match count.
  useEffect(() => {
    const q = debounced.trim();
    if (q.length < 2) {
      setRows([]);
      setTotal(0);
      return;
    }
    let cancelled = false;
    (async () => {
      let matched: LookupRow[] = [];
      if (isWorkspace) {
        const lower = q.toLowerCase();
        matched = workspaces
          .filter(
            (w) =>
              w.name.toLowerCase().includes(lower) ||
              w.urlPath.toLowerCase().includes(lower),
          )
          .map((w) => ({
            id: w.id,
            primary: w.name,
            secondary: `/${w.urlPath}`,
          }));
      } else {
        try {
          const res = await loginFetch(
            `/oAuth/users/search?query=${encodeURIComponent(q)}`,
          );
          const body = await res.json();
          matched = (body.users ?? []).map((u: SearchResult) => ({
            id: u.userId,
            primary: u.name || u.email,
            secondary: u.email,
          }));
        } catch {
          matched = [];
        }
      }
      if (cancelled) return;
      setTotal(matched.length);
      setRows(matched.slice(0, 10));
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, isWorkspace, workspaces, loginFetch]);

  // Resolve each visible row's live flag status. Re-runs when the rows change
  // or when fetchStatuses changes (the flag was toggled / its rollout edited) —
  // re-resolving statuses only, without re-hitting the user-search endpoint.
  useEffect(() => {
    fetchStatuses(rows.map((r) => r.id));
  }, [rows, fetchStatuses]);

  // An override write only touches the scope table, never the flag row, so we
  // refresh the visible statuses + the override tally — not the whole catalog.
  const refresh = useCallback(() => {
    fetchStatuses(rows.map((r) => r.id));
    refreshCount();
  }, [rows, fetchStatuses, refreshCount]);

  const setOverride = useCallback(
    async (id: string, enabled: boolean) => {
      const existing = statuses[id]?.overrideId ?? null;
      try {
        if (existing) {
          await flagsGql(AdminUpdateFeatureFlagScopeDocument, {
            id: existing,
            input: { enabled },
          });
        } else {
          await flagsGql(AdminCreateFeatureFlagScopeDocument, {
            input: {
              key: flag.key,
              name: flag.name,
              description: flag.description,
              scopeType: flag.scope,
              scopeId: id,
              enabled,
            },
          });
        }
        refresh();
      } catch (err) {
        toastMutationError(err, "Failed to set override");
      }
    },
    [statuses, flag, flagsGql, refresh],
  );

  const clearOverride = useCallback(
    async (id: string) => {
      const existing = statuses[id]?.overrideId;
      if (!existing) return;
      try {
        await flagsGql(AdminDeleteFeatureFlagScopeDocument, { id: existing });
        refresh();
      } catch (err) {
        toastMutationError(err, "Failed to clear override");
      }
    },
    [statuses, flagsGql, refresh],
  );

  // Delete every override row for this flag's current scope type, reverting
  // all of them to the rollout default. Gated behind a confirm — it's bulk and
  // irreversible.
  const removeAllOverrides = useCallback(async () => {
    setConfirmRemoveAll(false);
    try {
      const mine = await fetchMyScopes();
      await Promise.all(
        mine.map((s) =>
          flagsGql(AdminDeleteFeatureFlagScopeDocument, { id: s.id }),
        ),
      );
      refresh();
    } catch (err) {
      toastMutationError(err, "Failed to remove overrides");
    }
  }, [fetchMyScopes, flagsGql, refresh]);

  const overrideTotal = overrideCounts.enabled + overrideCounts.disabled;

  const searching = debounced.trim().length >= 2;

  return (
    <Box gap="sm">
      <Box direction="row" align="start" gap="sm">
        <Box fill="flex" gap="2xs">
          <Text size="2xs" weight="semibold" variant="muted">
            TARGETING
          </Text>
          <Text size="xs">
            <Text size="xs" variant="muted">{"Rollout enabled for "}</Text>
            <Text size="xs" weight="medium">{rolloutReach}</Text>
          </Text>
          <Text size="xs">
            <Text size="xs" variant="muted">{"Overrides — enabled for "}</Text>
            <Text size="xs" weight="medium" variant="success">
              {overrideCounts.enabled}
            </Text>
            <Text size="xs" variant="muted">{", disabled for "}</Text>
            <Text size="xs" weight="medium">{overrideCounts.disabled}</Text>
          </Text>
          <Text size="xs" variant="muted">
            {`Look up a ${isWorkspace ? "workspace" : "user"} to see whether the flag is on for them and override it.`}
          </Text>
        </Box>
        {overrideTotal > 0 ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmRemoveAll(true)}
          >
            <Icon name="x" size="sm" color="destructive" />
            Remove all overrides
          </Button>
        ) : null}
      </Box>

      <AlertDialog open={confirmRemoveAll} onOpenChange={setConfirmRemoveAll}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all overrides?</AlertDialogTitle>
            <AlertDialogDescription>
              {`This deletes all ${overrideTotal} ${
                isWorkspace ? "workspace" : "user"
              } override${overrideTotal === 1 ? "" : "s"} for "${flag.name}", reverting them to the rollout default. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button variant="destructive" onClick={removeAllOverrides}>
              Remove all
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>

      <InputGroup>
        <InputGroupAddon>
          <Icon name="search" />
        </InputGroupAddon>
        <InputGroupInput
          type="search"
          value={search}
          onValueChange={setSearch}
          placeholder={`Search ${unit} by name…`}
        />
      </InputGroup>

      {searching ? (
        <Box gap="2xs">
          <Text size="xs" variant="muted" mono>
            {total === 0
              ? `No ${unit} found`
              : total > rows.length
                ? `${total} ${unit} · showing first ${rows.length}`
                : `${total} ${total === 1 ? unit.slice(0, -1) : unit}`}
          </Text>
          {rows.map((r, i) => {
            const st = statuses[r.id];
            const meta = st ? SOURCE_META[st.source] : undefined;
            // The segmented state follows the override entity directly: no row
            // → Rollout; otherwise the override's on/off value.
            const overrideState =
              st?.overrideId == null ? "rollout" : st.enabled ? "on" : "off";
            return (
              <Box
                key={`${r.id}-${i}`}
                direction="row"
                align="center"
                gap="sm"
                paddingX="sm"
                paddingY="xs"
                border
                rounded="md"
              >
                {/* Workspaces have no entity-chip kind, so they keep the
                    initials mark; users render through the entity system. */}
                {isWorkspace ? (
                  <Box fill="flex" direction="row" align="center" gap="sm" minWidth={0}>
                    <InitialsAvatar name={r.primary} size="xs" shape="square" />
                    <Box minWidth={0}>
                      <Text size="sm" weight="medium" truncate={1}>
                        {r.primary}
                      </Text>
                      {r.secondary ? (
                        <Text
                          size="xs"
                          variant="muted"
                          mono
                          truncate={1}
                        >
                          {r.secondary}
                        </Text>
                      ) : null}
                    </Box>
                  </Box>
                ) : (
                  <Box fill="flex" minWidth={0}>
                    <EntityTag
                      entity={{
                        kind: "user",
                        id: r.id,
                        user: {
                          name: r.primary,
                          email: r.secondary !== r.primary ? r.secondary : null,
                        },
                      }}
                      subtitle
                      interactive={false}
                      size="sm"
                    />
                  </Box>
                )}
                {st ? (
                  <Badge variant={st.inRollout ? "info" : "secondary"}>
                    <Text size="2xs">
                      {st.inRollout ? "In rollout" : "Not in rollout"}
                    </Text>
                  </Badge>
                ) : null}
                {meta ? (
                  <Box direction="row" align="center" gap="xs">
                    <StatusDot background={meta.dot} />
                    <Text size="xs" variant={meta.tone}>
                      {meta.label}
                    </Text>
                  </Box>
                ) : null}
                <Segmented
                  alignSelf="center"
                  value={overrideState}
                  onChange={(v) => {
                    if (v === "rollout") {
                      if (st?.overrideId) clearOverride(r.id);
                    } else {
                      setOverride(r.id, v === "on");
                    }
                  }}
                  options={[
                    { value: "rollout", label: "Rollout" },
                    { value: "off", label: "Off" },
                    { value: "on", label: "On" },
                  ]}
                />
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Detail view — panel
// ---------------------------------------------------------------------------

function FlagDetailPanel({
  flag,
  workspaces,
  patchFlag,
  fillColor,
}: {
  flag: DbFlag;
  workspaces: AdminWorkspace[];
  patchFlag: (flag: DbFlag, patch: FlagPatch) => void;
  fillColor: Color;
}) {
  const [rolloutText, setRolloutText] = useState(String(flag.rolloutPercentage));
  useEffect(() => {
    setRolloutText(String(flag.rolloutPercentage));
  }, [flag.rolloutPercentage, flag.key]);

  const commitRollout = useCallback(() => {
    const n = Math.max(0, Math.min(100, parseInt(rolloutText, 10) || 0));
    setRolloutText(String(n));
    if (n !== flag.rolloutPercentage) patchFlag(flag, { rolloutPercentage: n });
  }, [rolloutText, flag, patchFlag]);

  return (
    <Box fill="flex" overflow="auto" padding="lg" gap="lg">
      {/* Header */}
      <Box direction="row" align="start" gap="md">
        <Box fill="flex" gap="xs">
          <Box direction="row" align="center" gap="sm">
            <Text size="lg" weight="semibold">
              {flag.name}
            </Text>
            <Badge variant={flag.enabled ? "success" : "secondary"}>
              <Text size="xs">{flag.enabled ? "On" : "Off"}</Text>
            </Badge>
          </Box>
          {/* The row wrapper is what makes the badge hug: as a direct child of
              the column above it is a stretch-aligned flex item and paints a
              full-width bar. Same idiom as the On/Off badge's row. */}
          <Box direction="row">
            <Badge variant="info">
              <Text size="xs" mono>
                {flag.key}
              </Text>
            </Badge>
          </Box>
          {flag.description ? (
            <Text size="sm" variant="muted">
              {flag.description}
            </Text>
          ) : null}
        </Box>
        <Switch
          checked={flag.enabled}
          onCheckedChange={(v) => patchFlag(flag, { enabled: v })}
        />
      </Box>

      {/* Scope */}
      <Box gap="xs">
        <Text size="2xs" weight="semibold" variant="muted">
          SCOPE
        </Text>
        <Segmented
          value={flag.scope}
          onChange={(v) => patchFlag(flag, { scope: v as FlagScopeType })}
          options={[
            { value: "user", label: "User", icon: "user" },
            { value: "workspace", label: "Workspace", icon: "layout-grid" },
          ]}
        />
        <Text size="xs" variant="muted">
          A flag targets either individual users or whole workspaces — not both.
          Its rollout % and overrides apply to the selected scope.
        </Text>
      </Box>

      {/* Rollout */}
      <Box gap="sm">
        <Box direction="row" align="center" justify="between">
          <Text size="2xs" weight="semibold" variant="muted">
            PERCENTAGE ROLLOUT
          </Text>
          <Text size="xl" weight="semibold" mono>
            {flag.rolloutPercentage}%
          </Text>
        </Box>
        <RolloutBar value={flag.rolloutPercentage} color={fillColor} thick />
        <Box direction="row" align="center" gap="xs">
          <Segmented
            value={
              ROLLOUT_PRESETS.includes(flag.rolloutPercentage)
                ? String(flag.rolloutPercentage)
                : ""
            }
            onChange={(v) =>
              patchFlag(flag, { rolloutPercentage: parseInt(v, 10) })
            }
            options={ROLLOUT_PRESETS.map((p) => ({
              value: String(p),
              label: `${p}%`,
            }))}
          />
          <Box width={80}>
            {/* The number field commits on Enter through the key handler and
                on blur. */}
            <Input
              value={rolloutText}
              onChange={(e) => setRolloutText(e.target.value)}
              onBlur={commitRollout}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRollout();
              }}
              inputMode="numeric"
              size="sm"
            />
          </Box>
        </Box>
      </Box>

      {/* Targeting */}
      <TargetingSection flag={flag} workspaces={workspaces} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function FeatureFlagsScreen({
  flags,
  loading,
  workspaces,
  refetchFlags,
}: {
  flags: DbFlag[];
  loading: boolean;
  workspaces: AdminWorkspace[];
  refetchFlags: () => void;
}) {
  const [view, setView] = useState<"list" | "detail">("detail");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const { patchFlag } = useFlagMutations(refetchFlags);

  const selected = useMemo(
    () => flags.find((f) => f.key === selectedKey) ?? flags[0] ?? null,
    [flags, selectedKey],
  );

  const partialCount = useMemo(
    () => flags.filter((f) => f.enabled && f.rolloutPercentage < 100).length,
    [flags],
  );

  return (
    <Box direction="column" fill="flex" minHeight={0}>
      <ScreenHeader
        title="Feature flags"
        meta={`${flags.length} flags · ${partialCount} partial rollouts`}
        actions={
          <Segmented
            value={view}
            onChange={(v) => setView(v as "list" | "detail")}
            options={[
              { value: "list", label: "List", icon: "list" },
              { value: "detail", label: "Detail", icon: "panel-right" },
            ]}
          />
        }
      />

      {view === "list" ? (
        <Box fill="flex" overflow="auto">
          <Box paddingX="lg" paddingBottom="xl" paddingTop="sm">
            <PanelCard>
              <TableHeaderRow>
                <HeaderLabel fill>Flag</HeaderLabel>
                <HeaderLabel width={120}>Status</HeaderLabel>
                <HeaderLabel width={150}>Rollout</HeaderLabel>
                <HeaderLabel width={120}>Scope</HeaderLabel>
              </TableHeaderRow>
              {!loading && flags.length === 0 ? (
                <Box paddingX="md" paddingY="lg" align="center">
                  <Text size="sm" variant="muted">
                    No feature flags found
                  </Text>
                </Box>
              ) : null}
              {flags.map((flag, index) => (
                <FlagListRow
                  key={`${flag.key}-${index}`}
                  flag={flag}
                  isLast={index === flags.length - 1}
                  fill={rolloutColor(flag)}
                  onToggle={(enabled) => patchFlag(flag, { enabled })}
                  onOpen={() => {
                    setSelectedKey(flag.key);
                    setView("detail");
                  }}
                />
              ))}
            </PanelCard>
          </Box>
        </Box>
      ) : (
        <Box fill="flex" direction="row" minHeight={0} paddingX="lg" paddingBottom="lg" gap="md">
          {/* Master list */}
          <Box width={280} minHeight={0}>
            <PanelCard>
              {/* Flex column, not a block: the rows render as `<button>`, and a
                  block-level button is still shrink-to-fit — each row would
                  bound (and highlight) at its own label width. As flex items
                  they stretch to the card. */}
              <Box direction="column" overflow="auto">
                {flags.map((flag, index) => {
                  const active = selected?.key === flag.key;
                  return (
                    <Box
                      as="button"
                      type="button"
                      key={`${flag.key}-${index}`}
                      direction="row"
                      align="center"
                      gap="sm"
                      paddingX="sm"
                      paddingY="xs"
                      border={index === flags.length - 1 ? undefined : "bottom"}
                      background={active ? "muted" : undefined}
                      hoverBackground
                      cursor="pointer"
                      onClick={() => setSelectedKey(flag.key)}
                    >
                      <StatusDot background={flag.enabled ? "success" : "muted"} />
                      <Box fill="flex">
                        <Text size="sm" weight="medium" truncate={1}>
                          {flag.name}
                        </Text>
                        <Text size="2xs" variant="muted" mono truncate={1}>
                          {flag.key}
                        </Text>
                      </Box>
                      <Text size="2xs" variant="muted" mono>
                        {flag.rolloutPercentage}%
                      </Text>
                    </Box>
                  );
                })}
              </Box>
            </PanelCard>
          </Box>

          {/* Detail */}
          <Box fill="flex" minHeight={0}>
            <PanelCard>
              {selected ? (
                <FlagDetailPanel
                  flag={selected}
                  workspaces={workspaces}
                  patchFlag={patchFlag}
                  fillColor={rolloutColor(selected)}
                />
              ) : (
                <Box padding="lg" align="center">
                  <Text size="sm" variant="muted">
                    No flag selected
                  </Text>
                </Box>
              )}
            </PanelCard>
          </Box>
        </Box>
      )}
    </Box>
  );
}
